import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, map } from 'rxjs';
import { DataService } from './data.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface SelectedCity {
  id: number;
  name: string;
  country_name?: string;
}

export interface CityItem {
  id?: number;
  name?: string;
  country_name?: string;
  country?: string;
  events_count?: number;
  events?: number;
  flag?: string;
  [key: string]: any;
}

const CITY_ONBOARDING_KEY = 'livepass_city_onboarding';
const CITY_SELECTED_KEY = 'livepass_selected_city';

/** Client-assigned when API row has no id; must not be sent as `city_id` to backend. */
export const SYNTHETIC_CITY_ID_MIN = 1_000_000;

export function isBackendCityId(id: number | null | undefined): boolean {
  return id != null && !Number.isNaN(Number(id)) && Number(id) > 0 && Number(id) < SYNTHETIC_CITY_ID_MIN;
}

const CITY_HIDDEN_CHAR_REGEX = /[\u200B-\u200D\u2060\uFEFF]/g;
const DEFAULT_CITY_NAME = 'Delhi NCR';
@Injectable({
  providedIn: 'root'
})
export class CityService {
  private selectedCitySubject = new BehaviorSubject<SelectedCity | null>(null);
  public selectedCity$: Observable<SelectedCity | null> = this.selectedCitySubject.asObservable();

  private cityPickerOpenSubject = new BehaviorSubject<boolean>(false);
  public cityPickerOpen$: Observable<boolean> = this.cityPickerOpenSubject.asObservable();

  private citiesSubject = new BehaviorSubject<CityItem[]>([]);
  public cities$ = this.citiesSubject.asObservable();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public isLoadingCities$ = this.loadingSubject.asObservable();
  cities: CityItem[] = [];
  isLoadingCities = false;

  constructor(private dataService: DataService, private http: HttpClient) {
    this.hydrateSelectedCityFromStorage()
  }

  hasCompletedCityOnboarding(): boolean {
    if (typeof localStorage === 'undefined') {
      return true;
    }
    return localStorage.getItem(CITY_ONBOARDING_KEY) === '1';
  }

  // private hydrateSelectedCityFromStorage(): void {
  //   if (typeof localStorage === 'undefined' || !this.hasCompletedCityOnboarding()) {
  //     return;
  //   }
  //   try {
  //     const raw = localStorage.getItem(CITY_SELECTED_KEY);
  //     if (!raw) {
  //       return;
  //     }
  //     const c = this.normalizeSelectedCity(JSON.parse(raw) as Partial<SelectedCity> | null);
  //     if (c) {
  //       this.selectedCitySubject.next(c);
  //     }
  //   } catch {
  //     /* ignore */
  //   }
  // }
  private hydrateSelectedCityFromStorage(): void {
  let restored: SelectedCity | null = null;

  if (typeof localStorage !== 'undefined' && this.hasCompletedCityOnboarding()) {
    try {
      const raw = localStorage.getItem(CITY_SELECTED_KEY);
      if (raw) {
        restored = this.normalizeSelectedCity(JSON.parse(raw) as Partial<SelectedCity> | null);
      }
    } catch { /* ignore */ }
  }

  this.selectedCitySubject.next(restored ?? this.buildDefaultCity());
}

private buildDefaultCity(): SelectedCity {
  return {
    id: this.buildSyntheticCityId(DEFAULT_CITY_NAME),
    name: DEFAULT_CITY_NAME,
    country_name: 'India'
  };
}

  setSelectedCity(city: SelectedCity | null) {
    const resolved = this.resolveSelectedCity(city);
    this.selectedCitySubject.next(resolved);
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      if (!resolved) {
        localStorage.removeItem(CITY_SELECTED_KEY);
        localStorage.removeItem(CITY_ONBOARDING_KEY);
        return;
      }
      localStorage.setItem(CITY_SELECTED_KEY, JSON.stringify(resolved));
      localStorage.setItem(CITY_ONBOARDING_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  getSelectedCity(): SelectedCity | null {
    return this.selectedCitySubject.value;
  }

  openCityPicker(): void {
    this.loadCities();
    this.cityPickerOpenSubject.next(true);
  }

  closeCityPicker(): void {
    this.cityPickerOpenSubject.next(false);
  }

  getCities(): CityItem[] {
    return this.cities;
  }

  loadCities(): void {
    this.isLoadingCities = true;
    this.loadingSubject.next(true);
    this.dataService
      // Header requirement: always load full city list (not popular-only)
      .getCities(false)
      .pipe(
        switchMap((response) => {
          // console.log('[CityService] cities API response (is_popular=false):', response);
          let raw = this.extractCitiesArrayLikeDiscover(response);
          if (!raw.length) {
            raw = this.parseCitiesResponse(response);
          }
          let normalized = this.normalizeCityList(raw);
          // console.log('[CityService] normalized cities (is_popular=false):', {
          //   count: normalized.length,
          //   sample: normalized.slice(0, 10)
          // });
          if (normalized.length > 0) {
            return of(normalized);
          }
          // Fallback: try popular list if backend rejects is_popular=false
          return this.dataService.getCities(true).pipe(
            map((r2) => {
              // console.log('[CityService] cities API response (fallback is_popular=true):', r2);
              let r = this.extractCitiesArrayLikeDiscover(r2);
              if (!r.length) {
                r = this.parseCitiesResponse(r2);
              }
              const out = this.normalizeCityList(r);
              // console.log('[CityService] normalized cities (fallback is_popular=true):', {
              //   count: out.length,
              //   sample: out.slice(0, 10)
              // });
              return out;
            })
          );
        })
      )
      .subscribe({
        next: (citiesData) => {
          this.isLoadingCities = false;
          this.loadingSubject.next(false);
          this.cities = citiesData;
          this.citiesSubject.next(citiesData);
          // console.log('[CityService] cities loaded:', { count: citiesData.length, sample: citiesData.slice(0, 10) });

          const currentSelection = this.getSelectedCity();
          if (currentSelection) {
            const resolvedSelection = this.resolveSelectedCity(currentSelection);
            if (
              resolvedSelection &&
              (
                Number(resolvedSelection.id) !== Number(currentSelection.id) ||
                resolvedSelection.name !== currentSelection.name ||
                resolvedSelection.country_name !== currentSelection.country_name
              )
            ) {
              this.setSelectedCity(resolvedSelection);
            }
          }

          if (citiesData.length > 0 && !this.getSelectedCity() && this.hasCompletedCityOnboarding()) {
            const dubai = citiesData.find((c) => c.name?.toLowerCase().includes('dubai'));
            const first = dubai ?? citiesData[0];
            this.setSelectedCity({
              id: first.id!,
              name: first.name!,
              country_name: first.country_name
            });
          }
        },
        error: () => {
          this.isLoadingCities = false;
          this.loadingSubject.next(false);
          this.cities = [];
          this.citiesSubject.next([]);
        }
      });
  }

  /** Same as SearchComponent.loadCities() response handling. */
  private extractCitiesArrayLikeDiscover(response: any): any[] {
    if (!response) {
      return [];
    }
    let citiesData: any[] = [];
    if (response.code === 1 && response.data) {
      if (response.data.code === 1 && Array.isArray(response.data.data)) {
        citiesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        citiesData = response.data;
      }
    } else if (Array.isArray(response)) {
      citiesData = response;
    } else if (Array.isArray(response.data)) {
      citiesData = response.data;
    }
    return citiesData;
  }

  private parseCitiesResponse(response: any): any[] {
    if (!response) {
      return [];
    }
    if (response.code === 1 && response.data) {
      if (response.data.code === 1 && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
    }
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    if (response.cities && Array.isArray(response.cities)) {
      return response.cities;
    }
    if (response.result && Array.isArray(response.result)) {
      return response.result;
    }
    if (response.list && Array.isArray(response.list)) {
      return response.list;
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private normalizeCityList(raw: any[]): CityItem[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    const out: CityItem[] = [];
    raw.forEach((item, index) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const idRaw = item.id ?? item.city_id ?? item.cityId;
      const name =
        item.name ?? item.city_name ?? item.city ?? item.title ?? item.label;
      const cleanName = this.cleanCityText(name);
      if (!cleanName) {
        return;
      }
      let idNum: number;
      if (idRaw != null && idRaw !== '' && !Number.isNaN(Number(idRaw))) {
        idNum = Number(idRaw);
      } else {
        idNum = 1_000_000 + index;
      }
      const country = item.country_name ?? item.country ?? item.countryName;
      out.push({
        ...item,
        id: idNum,
        name: cleanName,
        country_name: country != null ? this.cleanCityText(country) || undefined : undefined
      });
    });
    return out;
  }

  resolveSelectedCity(city: Partial<SelectedCity & CityItem> | null | undefined): SelectedCity | null {
    if (!city) {
      return null;
    }

    const normalized = this.normalizeSelectedCity(city);
    if (!normalized) {
      return null;
    }

    const byId = isBackendCityId(normalized.id)
      ? this.cities.find((candidate) => Number(candidate.id) === Number(normalized.id))
      : null;
    if (byId?.name) {
      return {
        id: Number(byId.id),
        name: byId.name.trim(),
        country_name: byId.country_name
      };
    }

    const nameMatches = this.findCityMatches(normalized.name);
    const exactMatch = nameMatches.find((candidate) => {
      const candidateToken = this.normalizeCityToken(candidate.name);
      return this.getCityLookupTokens(normalized.name).includes(candidateToken);
    });
    const matched = exactMatch ?? nameMatches[0] ?? null;

    if (matched?.name) {
      return {
        id: Number(matched.id),
        name: matched.name.trim(),
        country_name: matched.country_name
      };
    }

    return normalized;
  }

  private normalizeSelectedCity(city: Partial<SelectedCity & CityItem> | null | undefined): SelectedCity | null {
    const name = this.cleanCityText(city?.name ?? city?.['city'] ?? city?.['city_name'] ?? '');
    if (!name) {
      return null;
    }

    const rawId = city?.id;
    const numericId = rawId != null && String(rawId).trim() !== '' && Number.isFinite(Number(rawId))
      ? Number(rawId)
      : this.buildSyntheticCityId(name);

    return {
      id: numericId,
      name,
      country_name: city?.country_name != null ? this.cleanCityText(city.country_name) || undefined : undefined
    };
  }

  private findCityMatches(name: string): CityItem[] {
    const candidateTokens = new Set(this.getCityLookupTokens(name));
    if (candidateTokens.size === 0) {
      return [];
    }

    return this.cities.filter((candidate) => {
      const candidateName = candidate?.name;
      if (!candidateName) {
        return false;
      }
      const normalizedCandidate = this.normalizeCityToken(candidateName);
      if (candidateTokens.has(normalizedCandidate)) {
        return true;
      }
      const candidateLookupTokens = this.getCityLookupTokens(candidateName);
      return candidateLookupTokens.some((token) => candidateTokens.has(token));
    });
  }

  private getCityLookupTokens(value: unknown): string[] {
    const base = this.normalizeCityToken(value);
    if (!base) {
      return [];
    }

    const tokens = new Set<string>([base, base.replace(/\s+/g, '')]);
    const rawParts = String(value ?? '')
      .split(/[,/|-]+/)
      .map((part) => this.normalizeCityToken(part))
      .filter(Boolean);

    rawParts.forEach((part) => {
      tokens.add(part);
      tokens.add(part.replace(/\s+/g, ''));

      const aliasesForPart = this.getCityAliases(part);
      aliasesForPart.forEach((alias) => {
        const normalizedAlias = this.normalizeCityToken(alias);
        if (!normalizedAlias) {
          return;
        }
        tokens.add(normalizedAlias);
        tokens.add(normalizedAlias.replace(/\s+/g, ''));
      });
    });

    const aliases = this.getCityAliases(base);
    aliases.forEach((alias) => {
      const normalizedAlias = this.normalizeCityToken(alias);
      if (!normalizedAlias) {
        return;
      }
      tokens.add(normalizedAlias);
      tokens.add(normalizedAlias.replace(/\s+/g, ''));
    });
    return [...tokens];
  }

  private getCityAliases(token: string): string[] {
    const aliases: Record<string, string[]> = {
      'new delhi': ['delhi', 'delhi ncr'],
      'delhi': ['new delhi', 'delhi ncr'],
      'delhi ncr': ['delhi', 'new delhi']
    };
    return aliases[token] ?? [];
  }

  private normalizeCityToken(value: unknown): string {
    return this.cleanCityText(value)
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private cleanCityText(value: unknown): string {
    return String(value ?? '')
      .replace(CITY_HIDDEN_CHAR_REGEX, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private buildSyntheticCityId(name: string): number {
    let hash = 0;
    const token = this.normalizeCityToken(name);
    for (let index = 0; index < token.length; index++) {
      hash = ((hash << 5) - hash + token.charCodeAt(index)) | 0;
    }
    return SYNTHETIC_CITY_ID_MIN + Math.abs(hash % 900_000);
  }

  detectLocation(): Observable<any> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return this.http.get<any>(`${environment.apiBaseUrl}/get/location`);
    }

    return new Observable<any>((observer) => {
      const requestCurrentPosition = () => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.http
              .get<any>(`${environment.apiBaseUrl}/get/location`, {
                params: {
                  lat: String(position.coords.latitude),
                  lng: String(position.coords.longitude)
                }
              })
              .subscribe({
                next: (response) => {
                  observer.next(response);
                  observer.complete();
                },
                error: (error) => observer.error(error)
              });
          },
          (error) => {
            const message =
              error.code === error.PERMISSION_DENIED
                ? 'Location access is blocked. Please enable location permission in your browser site settings and try again.'
                : error.code === error.POSITION_UNAVAILABLE
                  ? 'Your location is unavailable right now. Please try again.'
                  : error.code === error.TIMEOUT
                    ? 'Location detection timed out. Please try again.'
                    : 'We could not detect your location. Please try again.';

            observer.error(new Error(message));
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      };

      const permissionsApi = (navigator as Navigator & {
        permissions?: {
          query(input: { name: 'geolocation' }): Promise<{ state: 'granted' | 'denied' | 'prompt' }>;
        };
      }).permissions;

      if (!permissionsApi?.query) {
        requestCurrentPosition();
        return;
      }

      permissionsApi
        .query({ name: 'geolocation' })
        .then((status) => {
          if (status.state === 'denied') {
            observer.error(
              new Error('Location access is blocked in your browser. Reset site permission to prompt again.')
            );
            return;
          }
          requestCurrentPosition();
        })
        .catch(() => {
          requestCurrentPosition();
        });
    });
  }
}
